import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { ErrorState } from '../../../../components/ErrorState';
import { LazyRichTextField } from '../../../../components/inputs/LazyRichTextField';
import { TextField } from '../../../../components/inputs/TextField';
import { Modal } from '../../../../components/Modal';
import { Skeleton } from '../../../../components/Skeleton';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { surveyTrainingRecommendationsApi } from '../surveyApi';
import type { SurveyTrainingRecommendation } from '../surveyTypes';
import { sampleTrainingDetails, sampleTrainingTitle } from '../utils/sampleDevelopmentPlan';

const ID = 'training-recommendation';

/**
 * A training recommendation's form — create (from a plan's Training Recommendations) and edit.
 * A Title and the Details, a rich-text document. Writing takes `surveys.plan`; the server refuses
 * an edit while an approver holds it and once approved, and the caller does not open this then.
 *
 * Autogenerate fills the title AND the details (product call, 13 Oct 2026). The editor is
 * uncontrolled after mount, so the sample is shown by REMOUNTING it under a bumped `key`.
 */
export function SurveyTrainingRecommendationModal({
  open,
  planId,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  planId: string;
  /** Null creates. */
  editing: SurveyTrainingRecommendation | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();

  const [title, setTitle] = useState(editing?.title ?? '');
  // An edit loads the stored details before the editor mounts — it takes its value once.
  const [details, setDetails] = useState<string | null>(editing ? null : '');
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  // Bumped by Autogenerate: the editor takes its value ONCE, so a sample shows only in a fresh editor.
  const [editorKey, setEditorKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!editing) return;

    const controller = new AbortController();

    surveyTrainingRecommendationsApi
      .get(editing.id, controller.signal)
      .then((detail) => {
        if (!controller.signal.aborted) setDetails(detail.details ?? '');
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;

        setLoadError(caught instanceof ApiError ? caught : new ApiError('Could not load this training recommendation.', 0));
      });

    return () => controller.abort();
  }, [editing]);

  const ready = details !== null;
  const canSave = ready && title.trim() !== '' && confirmed && !saving;

  async function save() {
    if (!canSave || details === null) return;

    setSaving(true);
    setError(null);

    try {
      const request = { title: title.trim(), details };

      if (editing) await surveyTrainingRecommendationsApi.update(editing.id, request);
      else await surveyTrainingRecommendationsApi.create(planId, request);

      toast.success(editing ? 'Saved the training recommendation.' : 'Added the training recommendation.');
      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('The training recommendation could not be saved.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: ID });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? `Edit training recommendation — ${editing.title}` : 'New training recommendation'}
      size="xl"
      id={`${ID}-modal`}
      autogenerate={
        ready
          ? {
              onGenerate: () => {
                setTitle(sampleTrainingTitle());
                setDetails(sampleTrainingDetails());
                setEditorKey((k) => k + 1);
              },
              disabled: saving,
            }
          : undefined
      }
      confirm={ready ? { id: `${ID}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving } : undefined}
      footer={
        <>
          <Button id={`${ID}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id={`${ID}-submit`}
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      {loadError ? (
        <ErrorState message={loadError.message} correlationId={loadError.correlationId} />
      ) : !ready ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {error ? (
            <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
              <p className="text-xs text-red-700">{error.message}</p>
            </div>
          ) : null}

          <TextField
            id={`${ID}-title`}
            label="Title"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            error={error?.fieldError('title') ?? (title.trim() === '' ? 'Enter a title.' : undefined)}
            disabled={saving}
            maxLength={300}
            autoFocus
          />

          <LazyRichTextField
            key={editorKey}
            id={`${ID}-details`}
            label="Details"
            description="What is recommended, and why."
            value={details}
            onChange={setDetails}
            maxLength={8000}
            disabled={saving}
          />
        </div>
      )}
    </Modal>
  );
}
